/*
 * FP Rewards — the storefront's small moving parts.
 *
 * MOST OF THIS FEATURE IS NOT HERE. A shopper's balance, plays and referral
 * link are rendered by Liquid from their own Shopify customer metafields,
 * server-side, with no request at all. What is left is only what a server
 * render cannot do:
 *
 *   1. CAPTURE a referral code from the URL and attach it to the cart, so the
 *      orders/paid webhook can see it.
 *   2. COPY the referral link to the clipboard.
 *   3. REDEEM — the single write a shopper makes — through the app proxy, and
 *      only when the theme says the proxy exists.
 *   4. Add thousands separators, which Liquid has no filter for.
 *   5. THE DASHBOARD on /pages/rewards: ask the proxy for `summary` and
 *      `history`, and fill in what a metafield mirror cannot carry — the
 *      programme's live rates, the ledger, the play expiry, the birthday form.
 *
 * Loaded once from theme.liquid. Everything below no-ops when its subject is
 * absent, so the cost on an ordinary page view is one `location.search` read.
 *
 * NUMBERS ARE COMPUTED, NEVER TYPED. 1 FP is worth `cents_per_point` cents, a
 * dollar is `points_per_dollar` FP, the earn rate is `earn_rate_bp` — all of
 * them arrive from `summary`, and every figure this file writes derives from
 * them. The one place a literal appears is the skeleton it replaces.
 *
 * DOM is built with createElement/textContent only. Nothing reaching it is a
 * string today; that is a fact about today's inputs, not about the code, and
 * this page carries a checkout.
 */
(function () {
  'use strict';

  var ENDPOINT = '/apps/rewards';
  var STORE_KEY = 'fp_ref';
  var TTL_DAYS = 90;
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var SVG_NS = 'http://www.w3.org/2000/svg';

  /* ── 1. referral capture ──────────────────────────────────────────────────
   * The code has to survive from the click on a friend's link to the moment the
   * order is paid, which can be a week and a different page every time. Two
   * carriers, because each covers the other's gap:
   *
   *   · localStorage keeps it across visits on this device.
   *   · A CART ATTRIBUTE is what actually reaches the webhook — Shopify copies
   *     cart attributes onto the order as note_attributes.
   *
   * The cart attribute is rewritten on every page load while a code is stored,
   * not just at capture, because a cleared or expired cart silently drops it and
   * nothing would ever notice. That costs one small same-origin POST, and only
   * for the small minority of visitors who arrived through a referral link.
   *
   * A third carrier needs no code at all: Shopify records the session's first
   * URL as `landing_site`, and the webhook reads ?fp_ref from it as a fallback.
   * That one covers the shopper whose localStorage and cart are both gone.
   */
  function readCode() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var saved = JSON.parse(raw);
      if (!saved || !saved.code) return null;
      if (saved.exp && Date.now() > saved.exp) {
        localStorage.removeItem(STORE_KEY);
        return null;
      }
      return saved.code;
    } catch (e) { return null; }
  }

  function storeCode(code) {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({
        code: code, exp: Date.now() + TTL_DAYS * 86400000,
      }));
    } catch (e) { /* private mode: the cart attribute and landing_site still carry it */ }
  }

  function attachToCart(code) {
    var body = { attributes: {} };
    body.attributes.fp_ref = code;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).catch(function () { /* landing_site is the fallback */ });
  }

  /**
   * Shape check only. The database decides whether a code is REAL; this keeps
   * junk out of the cart and out of a POST, and bounds the length so a
   * 400-character query parameter never reaches a lookup.
   */
  function normalizeCode(raw) {
    if (typeof raw !== 'string') return null;
    var code = raw.trim().toUpperCase();
    return /^FP[0-9A-Z]{4,12}$/.test(code) ? code : null;
  }

  /**
   * FIRST TOUCH WINS.
   *
   * Overwriting would let the last link a shopper happened to click take credit
   * from the friend who actually sent them — and the last link is often the
   * brand's own retargeting ad, so the rule is not academic.
   */
  function chooseCode(stored, fromUrl) {
    if (stored) return stored;
    return normalizeCode(fromUrl);
  }

  function capture() {
    var fromUrl = null;
    try {
      fromUrl = new URLSearchParams(location.search).get('fp_ref');
    } catch (e) { fromUrl = null; }

    var stored = readCode();
    var chosen = chooseCode(stored, fromUrl);
    if (chosen && chosen !== stored) storeCode(chosen);
    if (chosen) attachToCart(chosen);
  }

  /* ── pure helpers (also published on window.fpRewards for the tests) ──── */

  function toNum(v, fallback) {
    var n = Number(v);
    return isFinite(n) ? n : fallback;
  }

  /** 1234 -> "1,234". Anything non-numeric stays as it was. */
  function formatInt(n) {
    var v = Number(n);
    if (!isFinite(v)) return String(n);
    return Math.round(v).toLocaleString('en-US');
  }

  /** Points -> cents, at the programme's live rate. */
  function pointsToCents(points, centsPerPoint) {
    return Math.round(toNum(points, 0) * toNum(centsPerPoint, 0));
  }

  /** 140 -> "$1.40"; -250 -> "-$2.50". Always two decimals: this is a balance. */
  function formatDollars(cents) {
    var v = toNum(cents, 0);
    var sign = v < 0 ? '-' : '';
    return sign + '$' + (Math.abs(v) / 100).toFixed(2);
  }

  /** 12300 -> "$123"; 12340 -> "$123.40". For buttons and chips. */
  function formatWhole(cents) {
    var v = toNum(cents, 0);
    if (v % 100 === 0) return (v < 0 ? '-' : '') + '$' + Math.abs(v) / 100;
    return formatDollars(v);
  }

  /** "2026-09-01T12:00:00Z" -> "Sep 1"; another year -> "Sep 1, 2027". Local
   *  time, like the rest of the store. The year matters exactly when it is
   *  not this one: "locked until Sep 1" without it reads as today. */
  function shortDate(iso, now) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    var ref = now instanceof Date ? now : new Date();
    var year = d.getFullYear() === ref.getFullYear() ? '' : ', ' + d.getFullYear();
    return MONTHS[d.getMonth()] + ' ' + d.getDate() + year;
  }

  /** How far a balance is toward its next whole dollar, as a 0-100 percent. */
  function ringPct(points, pointsPerDollar) {
    var per = Math.round(toNum(pointsPerDollar, 10));
    if (!(per > 0)) per = 10;
    var p = Math.max(0, Math.round(toNum(points, 0)));
    return Math.round((p % per) / per * 100);
  }

  function monthKey(d) {
    return d.getFullYear() + '-' + (d.getMonth() < 9 ? '0' : '') + (d.getMonth() + 1);
  }

  /**
   * The last `n` calendar months ending with `now`, each with the earned and
   * spent figures the history reported for it — or zero where it reported
   * nothing. The history is a sparse list (a month with no entries is simply
   * absent), so it cannot be drawn directly: six bars mean six months.
   */
  function monthBuckets(months, n, now) {
    var by = {};
    (months || []).forEach(function (m) {
      if (!m) return;
      var key = String(m.month || m.m || '');
      if (!/^\d{4}-\d{2}$/.test(key)) return;
      by[key] = { earned: toNum(m.earned, 0), spent: toNum(m.spent, 0) };
    });
    var out = [];
    var base = now instanceof Date ? now : new Date();
    for (var i = n - 1; i >= 0; i--) {
      var d = new Date(base.getFullYear(), base.getMonth() - i, 1);
      var key = monthKey(d);
      var hit = by[key] || { earned: 0, spent: 0 };
      out.push({ key: key, label: MONTHS[d.getMonth()], earned: hit.earned, spent: hit.spent });
    }
    return out;
  }

  var KIND_LABEL = {
    order: 'Order', game: 'Game', referral: 'Referral', birthday: 'Birthday',
    redeem: 'Redeemed', reverse: 'Reversed', expire: 'Expired', other: 'Points',
  };

  /** Which of the seven icons a ledger entry gets. Order matters: a referral
   *  reversal carries source 'referral' and must read as a reversal. */
  function activityKind(entry) {
    var t = String((entry && entry.entry_type) || '').toLowerCase();
    var s = String((entry && entry.source) || '').toLowerCase();
    if (t.indexOf('expire') > -1) return 'expire';
    if (t.indexOf('reverse') > -1 || t.indexOf('clawback') > -1) return 'reverse';
    if (t.indexOf('redeem') > -1 || s === 'redeem' || s === 'redemption') return 'redeem';
    if (s === 'game' || t.indexOf('game') > -1) return 'game';
    if (s === 'referral' || t.indexOf('referral') > -1) return 'referral';
    if (s === 'birthday' || t.indexOf('birthday') > -1) return 'birthday';
    if (s === 'order' || t.indexOf('order') > -1 || t.indexOf('purchase') > -1) return 'order';
    return 'other';
  }

  /** `+18 FP · Order · Sep 1`, split so the number can be set in bold. */
  function activityLabel(entry) {
    var kind = activityKind(entry);
    var pts = Math.round(toNum(entry && entry.points, 0));
    return {
      kind: kind,
      points: (pts > 0 ? '+' : (pts < 0 ? '-' : '')) + formatInt(Math.abs(pts)) + ' FP',
      text: KIND_LABEL[kind] + (entry && entry.at ? ' · ' + shortDate(entry.at) : ''),
      note: (entry && entry.note) ? String(entry.note) : '',
      negative: pts < 0,
    };
  }

  /** The "Ways to earn" values, every one derived from the summary's numbers,
   *  in the design's short forms — "5% back", "1 play / order", "50 FP + 1 play",
   *  "100 FP / year" — and the rate chip beside the title, "10 FP = $1". */
  function howYouEarn(s) {
    var out = {};
    var bp = toNum(s.earn_rate_bp, NaN);
    if (isFinite(bp)) out.shop = (Math.round(bp) / 100) + '% back';
    var ppo = toNum(s.plays_per_order, NaN);
    if (isFinite(ppo)) out.play = Math.round(ppo) + ' play' + (ppo === 1 ? '' : 's') + ' / order';
    var rr = toNum(s.referral_referrer_points, NaN);
    var rp = toNum(s.referral_referrer_plays, NaN);
    if (isFinite(rr)) {
      out.refer = formatInt(rr) + ' FP'
        + (isFinite(rp) && rp > 0 ? ' + ' + Math.round(rp) + ' play' + (rp === 1 ? '' : 's') : '');
    }
    var bd = toNum(s.birthday_points, NaN);
    if (isFinite(bd)) out.birthday = formatInt(bd) + ' FP / year';
    var cpp = toNum(s.cents_per_point, NaN);
    if (isFinite(cpp) && cpp > 0) out.rate = formatInt(Math.round(100 / cpp)) + ' FP = $1';
    return out;
  }

  /** The tooltips behind the cards whose sentence carries a number. The two
   *  without one (shop, birthday) keep the copy the section rendered. */
  function achievableGameMax(values) {
    if (!values || !Array.isArray(values.bags) || values.bags.length !== 6
      || !values.bags.every(Number.isFinite)
      || !['perfect', 'chain', 'max_chain', 'sky', 'max_throw', 'max_play', 'throws'].every(function (key) { return Number.isFinite(values[key]) && values[key] >= 0; })) return NaN;
    var best = Math.max.apply(null, values.bags) + values.perfect + values.chain * values.max_chain + values.sky;
    return Math.min(values.max_play, Math.min(values.max_throw, best) * values.throws);
  }

  function howTips(s) {
    var out = {};
    var maxPlay = achievableGameMax(s.values);
    var throws = toNum(s.values && s.values.throws, 3);
    if (isFinite(maxPlay)) {
      out.play = (throws === 3 ? 'Three throws a play; the sum of the three' : Math.round(throws) + ' throws a play; the sum of them')
        + ' is your FP. Up to ' + formatInt(maxPlay) + ' FP.';
    }
    var rr = toNum(s.referral_referrer_points, NaN);
    var rp = toNum(s.referral_referrer_plays, NaN);
    var re = toNum(s.referral_referee_points, NaN);
    if (isFinite(rr) && isFinite(re)) {
      var play = isFinite(rp) && rp > 0 ? ' — and you get a play.' : '.';
      out.refer = rr === re
        ? 'When a friend’s first order is paid, you both get ' + formatInt(re) + ' FP' + play
        : 'When a friend’s first order is paid, they get ' + formatInt(re) + ' FP and you get ' + formatInt(rr) + ' FP' + play;
    }
    return out;
  }

  /** The referral headline — "You both get 50 FP." — or the split when the two
   *  sides differ; '' until the numbers exist. */
  function referralOffer(s) {
    var rr = toNum(s.referral_referrer_points, NaN);
    var re = toNum(s.referral_referee_points, NaN);
    if (!isFinite(rr) || !isFinite(re)) return '';
    if (rr === re) return 'You both get ' + formatInt(re) + ' FP.';
    return formatInt(re) + ' FP for them, ' + formatInt(rr) + ' FP for you.';
  }

  /** "Bottom 1 · Middle 2 · Top 3 · Jackpot 5 FP" from the six bag values the
   *  server scores with. The rack is two columns by three shelves: bags 0–1 on
   *  the bottom, 2–3 in the middle, 4 top left, 5 the jackpot. */
  function legendText(values) {
    var bags = values && values.bags;
    if (!Array.isArray(bags) || bags.length < 6) return '';
    var n = bags.map(function (v) { return toNum(v, NaN); });
    for (var i = 0; i < 6; i++) if (!isFinite(n[i])) return '';
    return 'Bottom ' + formatInt(n[0]) + ' · Middle ' + formatInt(n[2]) + ' · Top ' + formatInt(n[4]) + ' · Jackpot ' + formatInt(n[5]) + ' FP';
  }

  /** Which of the design's three colours a bar gets: the best month is red,
   *  the current month gold, the rest sand. Best wins a tie with now. */
  function barKind(bucket, isLast, max) {
    var e = toNum(bucket && bucket.earned, 0);
    if (e > 0 && e === max) return 'best';
    if (isLast) return 'now';
    return '';
  }

  /** A bar's height as a percentage of the tallest, never under 4. */
  function barHeight(earned, max) {
    var e = toNum(earned, 0), m = toNum(max, 0);
    if (!(m > 0)) return 4;
    return Math.max(4, Math.round(e / m * 100));
  }

  /* ── 4. thousands separators ─────────────────────────────────────────────
   * Liquid has no filter for this and "1234 FP" reads worse than "1,234".
   * Progressive: the server already rendered the correct number, so a failure
   * here leaves it correct and merely unformatted.
   */
  function formatNumbers() {
    var heads = document.querySelectorAll('.fp-rw-head, [data-fp-n]');
    for (var i = 0; i < heads.length; i++) {
      var match = /^(\d{4,})(\s.*)?$/.exec(heads[i].textContent || '');
      if (!match) continue;
      heads[i].textContent = formatInt(match[1]) + (match[2] || '');
    }
  }

  /* ── 2. copy the referral link ───────────────────────────────────────────── */
  function wireCopy(root) {
    var buttons = (root || document).querySelectorAll('[data-fp-copy]:not([data-fp-wired])');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute('data-fp-wired', '1');
      buttons[i].addEventListener('click', function (event) {
        var button = event.currentTarget;
        var value = button.getAttribute('data-fp-copy') || '';
        var was = button.textContent;
        var done = function () {
          button.textContent = 'Copied';
          setTimeout(function () { button.textContent = was; }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(value).then(done, function () { select(value); });
        } else {
          select(value);
        }
      });
    }
  }

  /** Clipboard denied (permissions, http, older Safari): select it so the
   *  shopper can copy it themselves rather than being told nothing happened. */
  function select(value) {
    var node = document.querySelector('[data-fp-link]');
    if (!node || !window.getSelection) return;
    node.textContent = value;
    var range = document.createRange();
    range.selectNodeContents(node);
    var selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  }

  /* ── 3. redeem ───────────────────────────────────────────────────────────
   * Only reachable when the theme rendered a redeem button, which it only does
   * when settings.fp_rewards_proxy is on. Without the app proxy this endpoint
   * does not exist, and a button that 404s is worse than no button.
   */
  function wireRedeem(root) {
    var buttons = (root || document).querySelectorAll('[data-fp-redeem]:not([data-fp-wired])');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute('data-fp-wired', '1');
      buttons[i].addEventListener('click', function (event) { redeem(event.currentTarget); });
    }
  }

  function el(tag, className, text, title) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    if (title) node.setAttribute('title', title);
    return node;
  }

  function api(qs, body) {
    var opt = { credentials: 'same-origin' };
    var controller = new AbortController();
    var timeout = setTimeout(function () { controller.abort(); }, 12000);
    opt.signal = controller.signal;
    if (body) {
      opt.method = 'POST';
      opt.headers = { 'content-type': 'application/json' };
      opt.body = JSON.stringify(body);
    }
    return fetch(ENDPOINT + qs, opt)
      .then(function (r) { return r.json().catch(function () { return null; }); })
      .catch(function () { return null; })
      .finally(function () { clearTimeout(timeout); });
  }

  function redeem(button) {
    // Disabled FIRST, before anything async. A double click on a slow connection
    // is the ordinary case, and the second click would reserve a second helping
    // of points against a balance the first had not yet spent.
    if (button.disabled) return;
    button.disabled = true;
    var was = button.textContent;
    button.textContent = 'Working…';

    api('?action=redeem', { points: Number(button.getAttribute('data-fp-redeem')) })
      .then(function (body) {
        var cents = body && Number(body.credit_amount_cents);
        var host = button.parentNode;
        if (body && body.ok === true && isFinite(cents) && host) {
          while (host.firstChild) host.removeChild(host.firstChild);
          host.appendChild(el('span', 'fp-rw-head', formatDollars(cents) + ' credit added'));
          host.appendChild(el('span', 'fp-rw-note', 'Use it at checkout',
            'Store credit appears as a payment option at checkout once you are signed in.'));
          // The balance moved: the dashboard re-reads it rather than guessing.
          if (dash.root) refresh(dash.root, { keepAct: true });
          return;
        }
        button.disabled = false;
        button.textContent = was;
        // Named, not generic. "Something went wrong" leaves a shopper unsure
        // whether their points are gone. setAttribute never parses markup, so a
        // hostile error string is inert here.
        if (host) {
          host.setAttribute('data-fp-error', (body && body.error) || 'unreachable');
          var message = host.querySelector('[data-fp-redeem-error]');
          if (!message) {
            message = el('p', 'fp-rw-note');
            message.setAttribute('data-fp-redeem-error', '');
            message.setAttribute('role', 'alert');
            host.appendChild(message);
          }
          var page = host.closest('[data-conversion-error]');
          message.textContent = page ? page.getAttribute('data-conversion-error') : 'Could not confirm the conversion. Refresh your balance before trying again.';
        }
      });
  }

  /* ── 5. the dashboard ────────────────────────────────────────────────────
   * The section rendered every label and every server-mirrored figure. What
   * arrives here is the live answer: `summary` for the rates and the account,
   * `history` for the bars and the ledger. The two are fetched in parallel and
   * each renders on its own, so a slow ledger never holds the balance hostage.
   */
  var dash = { root: null, summary: null };

  function q(root, sel) { return root.querySelector(sel); }
  function setText(root, sel, text) {
    var node = q(root, sel);
    if (node) node.textContent = text;
    return node;
  }
  function clear(node) { while (node && node.firstChild) node.removeChild(node.firstChild); }

  function svg(tag, attrs) {
    var node = document.createElementNS(SVG_NS, tag);
    for (var k in attrs) if (Object.prototype.hasOwnProperty.call(attrs, k)) node.setAttribute(k, String(attrs[k]));
    return node;
  }

  var ICONS = {
    order: 'M6 8h12l-1 12H7L6 8zM9 8V6a3 3 0 0 1 6 0v2',
    game: 'M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9L12 3z',
    referral: 'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.5 1.5M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.5-1.5',
    birthday: 'M5 20h14v-7H5zM5 13V9h14v4M12 9V6M9 9V7M15 9V7',
    redeem: 'M4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z',
    reverse: 'M9 14L4 9l5-5M4 9h10a6 6 0 0 1 0 12h-3',
    expire: 'M6 3h12M6 21h12M8 3v4l4 5 4-5V3M8 21v-4l4-5 4 5v4',
    other: 'M12 5v14M5 12h14',
  };

  function icon(kind, negative) {
    var box = el('span', 'fpd-act-ico' + (negative ? ' fpd-act-ico--minus' : ''));
    var s = svg('svg', { viewBox: '0 0 24 24', 'aria-hidden': 'true' });
    s.appendChild(svg('path', { d: ICONS[kind] || ICONS.other }));
    box.appendChild(s);
    return box;
  }

  function renderSummary(root, s) {
    dash.summary = s;
    var scope = dash.scope || root;
    var cpp = toNum(s.cents_per_point, 10);
    var perDollar = cpp > 0 ? Math.round(100 / cpp) : 10;
    var points = Math.round(toNum(s.balance_points, 0));
    // The stage prices its demo throws at the live rate, never a typed one.
    window.fpRewards.centsPerPoint = cpp;

    // balance ring and figures
    setText(scope, '[data-fp-balance]', formatInt(points));
    var ring = q(scope, '[data-fp-ring]');
    if (ring) ring.setAttribute('stroke-dasharray', ringPct(points, perDollar) + ' 100');
    setText(scope, '[data-fp-ring-label]', (Math.max(0, points) % perDollar) + '/' + perDollar);
    var worth = q(scope, '[data-fp-worth]');
    if (worth) {
      clear(worth);
      if (points > 0 || toNum(s.paid_order_count, 0) > 0) {
        worth.appendChild(document.createTextNode('Points value: '));
        var bold = el('b', null, formatDollars(pointsToCents(points, cpp)));
        bold.style.color = '#FFF9EF';
        worth.appendChild(bold);
        worth.appendChild(document.createTextNode(' store credit'));
        worth.setAttribute('title', '1 FP is ' + formatDollars(cpp) + ' of store credit.');
      } else {
        worth.textContent = 'Your points start with your first order';
        worth.setAttribute('title', 'An account is created the first time an order is paid, so points start with your next order rather than retroactively.');
      }
    }

    // the redeem action: a button for what is redeemable now, a note otherwise
    var act = q(scope, '[data-fp-act]');
    if (act && !dash.keepAct) {
      var redeemable = Math.round(toNum(s.redeemable_cents, 0));
      var floorPts = Math.round(toNum(s.redemption_floor_points, perDollar));
      clear(act);
      if (redeemable > 0 && s.redemption_enabled !== false && root.getAttribute('data-proxy') === '1') {
        var b = el('button', 'fprw-redeem', 'Convert ' + formatWhole(redeemable),
          'Converts whole dollars of FP into Shopify store credit. Credit is a payment method, so it stacks with any code and never lowers the cart total that earns free shipping.');
        b.type = 'button';
        b.setAttribute('data-fp-redeem', String(Math.round(redeemable / cpp)));
        act.appendChild(b);
        wireRedeem(scope);
      } else if (redeemable > 0) {
        act.appendChild(el('span', 'fprw-note', formatWhole(redeemable) + ' ready — redeeming opens soon',
          'Your points are banked and counting. Converting them to store credit opens shortly.'));
      } else {
        act.appendChild(el('span', 'fprw-note',
          'Convert from ' + formatInt(floorPts) + ' FP · whole dollars',
          'Store credit is redeemed in whole dollars, so the first redemption opens at ' + formatInt(floorPts) + ' FP.'));
      }
    }
    dash.keepAct = false;

    // plays: the card, and the hero's Play now — both carry data-fp-play
    var plays = Math.max(0, Math.round(toNum(s.plays_available, 0)));
    setText(scope, '[data-fp-plays]', formatInt(plays));
    setText(scope, '[data-fp-plays-word]', plays === 1 ? 'play' : 'plays');
    if (root.getAttribute('data-game') === '1') {
      var ctas = scope.querySelectorAll('[data-fp-play]');
      for (var c = 0; c < ctas.length; c++) {
        if (plays > 0) ctas[c].removeAttribute('aria-disabled');
        else ctas[c].setAttribute('aria-disabled', 'true');
      }
      setText(scope, '[data-fp-cta-label]', plays > 0
        ? 'Play now · ' + formatInt(plays) + (plays === 1 ? ' play' : ' plays')
        : 'No plays yet');
      var note = q(scope, '[data-fp-plays-note]');
      if (note) {
        if (plays === 0) {
          note.textContent = 'Your next order earns a play';
        } else {
          var throws = toNum(s.values && s.values.throws, 3);
          var maxPlay = achievableGameMax(s.values);
          var exp = s.plays_next_expiry ? shortDate(s.plays_next_expiry) : '';
          note.textContent = Math.round(throws) + ' throws each'
            + (isFinite(maxPlay) ? ' · up to ' + formatInt(maxPlay) + ' FP' : '')
            + (exp ? ' · next expires ' + exp : '');
        }
      }
    }

    // the stand: every pill and the legend from the values the server scores with
    var bags = s.values && s.values.bags;
    if (Array.isArray(bags)) {
      var bagEls = scope.querySelectorAll('[data-bag]');
      for (var i = 0; i < bagEls.length; i++) {
        var idx = toNum(bagEls[i].getAttribute('data-bag'), NaN);
        var pill = q(bagEls[i], '[data-fp-bag-val]');
        var value = toNum(bags[idx], NaN);
        if (pill && isFinite(value)) pill.textContent = formatInt(value) + ' FP';
      }
    }
    var legend = legendText(s.values);
    if (legend) setText(scope, '[data-fp-legend]:not([data-fp-program-copy])', legend);

    // referral
    var offer = referralOffer(s);
    if (offer) setText(scope, '[data-fp-offer]', offer);
    var invited = Math.round(toNum(s.referrals_invited, 0));
    var stat = q(scope, '[data-fp-refstat]');
    if (stat) {
      clear(stat);
      if (invited > 0) {
        stat.appendChild(el('b', null, formatInt(invited)));
        stat.appendChild(document.createTextNode(' joined'));
        var earned = Math.round(toNum(s.referral_earned_cents, 0));
        if (earned > 0) stat.appendChild(document.createTextNode(' · ' + formatWhole(earned) + ' earned'));
      }
    }

    // birthday
    var bp = toNum(s.birthday_points, NaN);
    var bpNode = q(scope, '[data-fp-bday-pts]');
    if (bpNode) bpNode.textContent = isFinite(bp) ? '+' + formatInt(bp) + ' FP' : '';
    applyProfile(root, s);

    // ways to earn: the value in each card, the number-carrying tips, the rate chip
    var how = howYouEarn(s);
    var tips = howTips(s);
    ['shop', 'play', 'refer', 'birthday', 'rate'].forEach(function (k) {
      var cell = q(scope, '[data-fp-how="' + k + '"]');
      if (cell) cell.textContent = how[k] || '—';
      var card = q(scope, '[data-fp-how-card="' + k + '"]');
      if (card && tips[k]) card.setAttribute('title', tips[k]);
    });

    formatNumbers();
  }

  /** The birthday card, from `summary` or from a fresh `profile_set` answer:
   *  both carry the same five profile keys. */
  function applyProfile(root, p) {
    var scope = dash.scope || root;
    var status = q(scope, '[data-fp-bday-status]');
    var form = q(scope, '[data-fp-bday]');
    var month = Math.round(toNum(p.dog_birthday_month, 0));
    var day = Math.round(toNum(p.dog_birthday_day, 0));
    var name = p.dog_name ? String(p.dog_name) : '';
    var bp = toNum(dash.summary && dash.summary.birthday_points, NaN);
    if (status) {
      if (month >= 1 && month <= 12 && day >= 1) {
        // "Biscuit’s birthday · Mar 14", the date in the brand red, as designed
        clear(status);
        status.appendChild(document.createTextNode((name || 'Your dog') + '’s birthday · '));
        var when = el('b', null, MONTHS[month - 1] + ' ' + day);
        when.style.color = '#D2232A';
        status.appendChild(when);
        status.hidden = false;
      } else if (isFinite(bp)) {
        status.textContent = 'Set the date once — ' + formatInt(bp) + ' FP lands every year';
        status.hidden = false;
      }
    }
    if (!form) return;
    if (name && form.elements.dog_name && !form.elements.dog_name.value) form.elements.dog_name.value = name;
    if (month && form.elements.month && !form.elements.month.value) form.elements.month.value = String(month);
    if (day && form.elements.day && !form.elements.day.value) form.elements.day.value = String(day);
    var year = Math.round(toNum(p.dog_birth_year, 0));
    if (year && form.elements.year && !form.elements.year.value) form.elements.year.value = String(year);
    var locked = p.birthday_locked_until ? new Date(p.birthday_locked_until) : null;
    var isLocked = !!(locked && !isNaN(locked.getTime()) && locked.getTime() > Date.now());
    ['month', 'day', 'year'].forEach(function (k) {
      var f = form.elements[k];
      if (!f) return;
      f.disabled = isLocked;
      var label = f.parentNode;
      if (label && label.setAttribute) {
        if (isLocked) label.setAttribute('title', 'Locked until ' + shortDate(p.birthday_locked_until) + ' — the name can change any time.');
        else if (k !== 'year') label.removeAttribute('title');
        if (label.classList) label.classList.toggle('fprw-field-locked', isLocked);
      }
    });
    form.setAttribute('data-fp-locked', isLocked ? '1' : '0');
    var lockNote = q(scope, '[data-fp-bday-lock]');
    if (lockNote) {
      lockNote.hidden = !isLocked;
      lockNote.textContent = isLocked ? String(form.getAttribute('data-lock-message') || '').replace('{date}', shortDate(p.birthday_locked_until)) : '';
    }
  }

  var BDAY_ERRORS = {
    bad_date: 'That date does not exist',
    birthday_locked: 'The birthday is locked for a year after it is set',
    no_account: 'Your rewards account opens with your first order',
    unreachable: 'Could not reach your rewards account',
  };

  function wireBirthday(root) {
    var form = q(root, '[data-fp-bday]');
    if (!form || form.getAttribute('data-fp-wired')) return;
    form.setAttribute('data-fp-wired', '1');
    var msg = q(root, '[data-fp-bday-msg]');
    var say = function (text, kind) {
      if (!msg) return;
      msg.textContent = text;
      msg.setAttribute('data-kind', kind || 'ok');
    };
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var btn = q(form, '[data-fp-bday-save]');
      if (!btn || btn.disabled) return;
      var name = String(form.elements.dog_name.value || '').trim();
      var month = Number(form.elements.month.value);
      var day = Number(form.elements.day.value);
      var year = form.elements.year && form.elements.year.value ? Number(form.elements.year.value) : null;
      if (!name) { say('Name your dog first', 'error'); form.elements.dog_name.focus(); return; }
      if (!(month >= 1 && month <= 12) || !(day >= 1 && day <= 31)) {
        if (form.getAttribute('data-fp-locked') === '1') {
          // Date locked and unchanged: the name is the only thing to save.
          month = Number(form.elements.month.value); day = Number(form.elements.day.value);
        }
        if (!(month >= 1 && month <= 12) || !(day >= 1 && day <= 31)) { say('Pick a month and a day', 'error'); return; }
      }
      if (day > [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]) {
        say(BDAY_ERRORS.bad_date, 'error'); form.elements.day.focus(); return;
      }
      btn.disabled = true;
      var was = btn.textContent;
      btn.textContent = 'Saving…';
      var body = { dog_name: name, month: month, day: day };
      if (year) body.year = year;
      api('?action=profile_set', body).then(function (r) {
        btn.disabled = false;
        btn.textContent = was;
        if (!r) { say(BDAY_ERRORS.unreachable + ' (unreachable)', 'error'); return; }
        if (!r.ok) {
          var code = String(r.error || 'error');
          var human = BDAY_ERRORS[code] || 'Could not save';
          if (code === 'birthday_locked' && r.birthday_locked_until) {
            human = 'Birthday changes are locked until ' + shortDate(r.birthday_locked_until);
          }
          say(human + ' (' + code + ')', 'error');
          return;
        }
        applyProfile(root, r);
        say('Saved', 'ok');
      });
    });
  }

  /** Clone the first element of a <template data-fp-tpl="…"> the section rendered. */
  function fromTemplate(scope, name) {
    var t = q(scope, 'template[data-fp-tpl="' + name + '"]');
    var node = t && t.content && t.content.firstElementChild;
    return node ? node.cloneNode(true) : null;
  }

  function renderHistory(root, h, s) {
    var scope = dash.scope || root;
    var chart = q(scope, '[data-fp-chart]');
    if (chart) {
      var buckets = monthBuckets(h.months, 6, new Date());
      var max = 0;
      buckets.forEach(function (b) { if (b.earned > max) max = b.earned; });
      var bars = q(chart, '.fprw-bars');
      var labels = q(chart, '.fprw-bar-labels');
      var empty = q(chart, '[data-fp-chart-empty]');
      if (max <= 0) {
        if (bars) bars.hidden = true;
        if (labels) labels.hidden = true;
        if (!empty) chart.appendChild(el('p', 'fprw-empty', 'No points yet — your first order starts the chart.'));
      } else if (bars && labels) {
        // The design's six columns: the number above each bar, the best month
        // red, the current month gold, the rest sand. Cloned from the templates
        // the section rendered, so the bars a refresh draws are the bars Liquid drew.
        clear(bars); clear(labels);
        buckets.forEach(function (b, i) {
          var kind = barKind(b, i === buckets.length - 1, max);
          var col = fromTemplate(scope, 'bar') || el('div');
          col.setAttribute('title', b.label + ' ' + b.key.slice(0, 4) + ' · earned ' + formatInt(b.earned) + ' FP' + (kind === 'now' ? ' so far' : ''));
          var n = q(col, '[data-fp-bar-n]');
          if (n) { n.textContent = formatInt(b.earned); n.style.color = kind === 'best' ? '#D2232A' : '#3D1409'; }
          var bar = q(col, '[data-fp-bar]');
          if (bar) {
            bar.style.height = barHeight(b.earned, max) + '%';
            bar.style.background = kind === 'best' ? '#D2232A' : kind === 'now' ? '#F6C453' : '#F0DBBB';
          }
          bars.appendChild(col);
          var lab = fromTemplate(scope, 'bar-label') || el('span');
          lab.textContent = b.label;
          lab.style.color = kind === 'best' ? '#D2232A' : kind === 'now' ? '#AE5609' : '#8A6A45';
          labels.appendChild(lab);
        });
        bars.hidden = false;
        labels.hidden = false;
        if (empty && empty.parentNode) empty.parentNode.removeChild(empty);
      }
    }

    var life = toNum(h.lifetime_earned, NaN);
    if (!isFinite(life) && s) life = toNum(s.lifetime_earned_points, NaN);
    var lifeNode = q(scope, '[data-fp-life]');
    if (lifeNode && isFinite(life) && life > 0) {
      clear(lifeNode);
      lifeNode.appendChild(document.createTextNode('Lifetime '));
      var lb = el('b', null, formatInt(life));
      lb.setAttribute('data-fp-n', '');
      lb.style.color = '#3D1409';
      lifeNode.appendChild(lb);
      lifeNode.appendChild(document.createTextNode(' FP'));
    }

    // the ledger: six rows, the rest behind "All →", never more than twelve
    var list = q(scope, '[data-fp-activity]');
    if (list) {
      clear(list);
      var entries = (h.entries || []).slice(0, 12);
      if (!entries.length) {
        list.appendChild(el('li', 'fprw-empty', 'Nothing yet — your first order starts it.'));
      }
      entries.forEach(function (e, i) {
        var a = activityLabel(e);
        var li = fromTemplate(scope, 'activity') || el('li', 'fprw-act');
        if (a.negative) li.classList.add('is-neg');
        if (a.note) li.setAttribute('title', a.note);
        var path = q(li, '[data-fp-act-d]');
        if (path) path.setAttribute('d', ICONS[a.kind] || ICONS.other);
        setText(li, '[data-fp-act-pts]', a.points);
        setText(li, '[data-fp-act-txt]', a.text);
        if (i >= 6) li.hidden = true;
        list.appendChild(li);
      });
      var more = q(scope, '[data-fp-activity-all]');
      if (more) {
        more.hidden = entries.length <= 6;
        if (!more.getAttribute('data-fp-wired')) {
          more.setAttribute('data-fp-wired', '1');
          more.addEventListener('click', function () {
            var rows = list.querySelectorAll('[data-fp-act-row]');
            for (var r = 0; r < rows.length; r++) rows[r].hidden = false;
            more.hidden = true;
          });
        }
      }
    }
  }

  /** The proxy did not answer: every skeleton becomes a dash, never a number. */
  function settle(root) {
    var scope = dash.scope || root;
    var skels = scope.querySelectorAll('.fprw-skel');
    for (var i = 0; i < skels.length; i++) {
      var host = skels[i].parentNode;
      if (!host) continue;
      if (host.classList && host.classList.contains('fprw-skel-row')) host.parentNode.removeChild(host);
      else host.textContent = '—';
    }
    var list = q(scope, '[data-fp-activity]');
    if (list && !list.firstChild) list.appendChild(el('li', 'fprw-empty', 'Could not reach your ledger — try again in a moment.'));
  }

  /** The dashboard root is the signed-in section; the hero above it carries the
   *  Play CTA, the bag pills and the legend, so hooks are looked up from the
   *  page's .fprw wrapper when there is one. */
  function scopeOf(root) {
    return (root && root.closest && root.closest('.fprw')) || root;
  }

  function refresh(root, opts) {
    dash.root = root;
    dash.scope = scopeOf(root);
    dash.keepAct = !!(opts && opts.keepAct);
    Promise.all([
      api('?action=summary'),
      api('?action=history&months=6&entries=12'),
    ]).then(function (res) {
      var s = res[0], h = res[1];
      if (s && s.ok) renderSummary(root, s);
      if (h && h.ok) renderHistory(root, h, s && s.ok ? s : null);
      settle(root);
    });
  }

  function dashboard() {
    var root = document.querySelector('[data-fp-dash]');
    if (!root) return;
    dash.root = root;
    dash.scope = scopeOf(root);
    wireBirthday(dash.scope);
    if (root.getAttribute('data-proxy') !== '1') return;
    refresh(root);
  }

  function boot() {
    capture();
    formatNumbers();
    wireCopy(null);
    wireRedeem(null);
    dashboard();

    // The cart drawer builds its footer from <template data-fp-if> blocks that
    // the theme's own runtime mounts LATER, so a wire-once boot would leave a
    // redeem button in the drawer permanently dead — present in the markup,
    // inert on click, and silent about it.
    if (typeof MutationObserver !== 'function') return;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) {
          formatNumbers();
          wireCopy(null);
          wireRedeem(null);
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Exposed for the theme's own diagnostics and for the two node tests
  // (tools/fp-rewards-capture.test.mjs, tools/fp-rewards-dashboard.test.mjs).
  // PURE decisions only: nothing reachable here can touch a cart, a balance or
  // the network.
  window.fpRewards = {
    normalizeCode: normalizeCode, chooseCode: chooseCode,
    formatInt: formatInt, formatDollars: formatDollars, formatWhole: formatWhole,
    pointsToCents: pointsToCents, ringPct: ringPct, shortDate: shortDate,
    monthBuckets: monthBuckets, activityKind: activityKind, activityLabel: activityLabel,
    howYouEarn: howYouEarn, howTips: howTips, referralOffer: referralOffer, legendText: legendText,
    barKind: barKind, barHeight: barHeight,
    // written by renderSummary once `summary` answers; read by fp-rewards-stage.js
    centsPerPoint: undefined,
  };
})();
